import React, { useState, useEffect } from "react";
import { Table, Input, Space, Spin, Button, Popconfirm, message, Tag } from "antd";
import _ from "lodash";
import moment from "moment";
import { SearchOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";

const CustomerMessages = () => {
  const userId = localStorage.getItem("userId");
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/customerMessage/allMessageList`);
      const sortedData = _.orderBy(res.data, ['messageId'], ['desc']);
      setData(sortedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const handleDelete = async (messageId) => {
      try {
          await axios.delete(`${BASE_URL}/customerMessage/deleteMessage/${messageId}`);
          message.success("Message deleted");
          fetchData();
      } catch (err) {
          message.error("Failed to delete message");
      }
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  // Helper to generate filter props WITHOUT the render function
  const getColumnSearchProps = (dataIndex, columnTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className="p-4">
        <Input
          placeholder={`Search ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          className="mb-2"
        />
        <Space>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} size="small" style={{ width: 90 }}>OK</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
  });

  const columns = [
    {
      title: "Date",
      dataIndex: "messageDateTime",
      width: 150,
      render: (text) => (
          <div style={{ fontSize: '12px', color: '#666' }}>
              {text ? moment(text).format("DD-MMM-YYYY") : '-'}<br/>
              {text ? moment(text).format("hh:mm A") : ''}
          </div>
      ),
      sorter: (a, b) => moment(a.messageDateTime).unix() - moment(b.messageDateTime).unix(),
    },
    {
      title: "User Details",
      width: 220,
      // 🟢 FIX: We separate the Search Props from the Render function
      ...getColumnSearchProps("customerName", "Name"),
      render: (_, r) => (
        <div>
            <span style={{ fontWeight: 'bold', display: 'block', fontSize: '14px' }}>
                {/* Highlight search text if matches */}
                {searchedColumn === 'customerName' && searchText ? (
                    <span style={{ backgroundColor: '#ffc069' }}>{r.customerName || "Guest"}</span>
                ) : (
                    r.customerName || "Guest"
                )}
            </span>
            
            {r.customerNo && (
                <a href={`tel:${r.customerNo}`} style={{ color: '#1890ff', fontSize: '13px', display: 'block', marginTop: '4px' }}>
                    📞 {r.customerNo}
                </a>
            )}
            
            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                ID: {r.userID || "N/A"}
            </div>
        </div>
      ),
    },
    {
      title: "Reason",
      dataIndex: "messageReason",
      width: 160,
      filters: [
        { text: 'General Inquiry', value: 'General Inquiry' },
        { text: 'Order Support', value: 'Order Support' },
        { text: 'Returns/Refund', value: 'Returns/Refund' },
        { text: 'Other', value: 'Other' },
      ],
      onFilter: (value, record) => record.messageReason && record.messageReason.indexOf(value) === 0,
      render: (tag) => {
          let color = 'geekblue';
          if (tag === 'Returns/Refund') color = 'volcano';
          if (tag === 'Order Support') color = 'green';
          if (tag === 'Other') color = 'default';
          return <Tag color={color}>{tag || 'Inquiry'}</Tag>;
      }
    },
    {
      title: "Message",
      dataIndex: "customerMessage",
      // Here we WANT the default render behavior for search highlighting, so we can keep it simple
      // or customize it like below:
      ...getColumnSearchProps("customerMessage", "Message"),
      render: (text) => (
          <div style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto', fontSize: '13px', color: '#333' }}>
               {searchedColumn === 'customerMessage' && searchText ? (
                    <span style={{ backgroundColor: '#ffc069' }}>{text}</span>
                ) : text}
          </div>
      )
    },
    {
        title: "Action",
        width: 100,
        render: (_, r) => (
            <Popconfirm title="Delete this message?" onConfirm={() => handleDelete(r.messageId)} okText="Yes" cancelText="No" okType="danger">
                <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
            </Popconfirm>
        )
    }
  ];

  return userId ? (
    <div style={{ padding: 20, background: 'white', borderRadius: 8 }}>
        <h2 style={{ marginBottom: 20 }}>Customer Inquiries</h2>
        {isLoading ? (
            <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
        ) : (
            <Table
                columns={columns}
                dataSource={data}
                rowKey="messageId"
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
                bordered
            />
        )}
    </div>
  ) : (
    <div style={{ padding: 20, textAlign: 'center' }}>Sign in to see messages</div>
  );
};

export default CustomerMessages;